/**
 * Keycloak Authorization Code + PKCE flow (realm `internal`). LoginPage kicks
 * off `beginSso()` (redirect to Keycloak); CallbackPage calls `completeSso()`
 * to exchange the returned code for tokens. Mirrors the OIDC handshake the
 * legacy datacentral SDK performs, without pulling in keycloak-js.
 */
import axios from 'axios';
import { env } from '@/config/env';

const VERIFIER_KEY = 'pkce_code_verifier';
const INTENDED_KEY = 'intended_url';

function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  bytes.forEach((b) => (str += String.fromCharCode(b)));
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function challengeFrom(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

const authorizeUrl = () =>
  `${env.keycloak.url}/realms/${env.keycloak.realm}/protocol/openid-connect/auth`;
const tokenUrl = () =>
  `${env.keycloak.url}/realms/${env.keycloak.realm}/protocol/openid-connect/token`;

/** Redirect the browser to Keycloak to sign in. */
export async function beginSso(intendedUrl = '/'): Promise<void> {
  const verifier = randomVerifier();
  const challenge = await challengeFrom(verifier);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(INTENDED_KEY, intendedUrl);

  const params = new URLSearchParams({
    client_id: env.keycloak.clientId,
    redirect_uri: env.keycloak.redirectUri,
    response_type: 'code',
    scope: 'openid profile email',
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(`${authorizeUrl()}?${params.toString()}`);
}

export interface SsoTokens {
  access_token: string;
  refresh_token?: string;
}

/** Exchange the `code` returned to the callback for tokens. */
export async function completeSso(code: string): Promise<SsoTokens> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY) ?? '';
  const res = await axios.post(
    tokenUrl(),
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: env.keycloak.clientId,
      redirect_uri: env.keycloak.redirectUri,
      code,
      code_verifier: verifier,
    }),
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
  );
  sessionStorage.removeItem(VERIFIER_KEY);
  return res.data;
}

/** The path the user was heading to before login bounced them (default '/'). */
export function popIntendedUrl(): string {
  const url = sessionStorage.getItem(INTENDED_KEY) || '/';
  sessionStorage.removeItem(INTENDED_KEY);
  return url;
}
